/**
 * Foundation/Anagrafica — sezione "Identità fiscale + Sede legale + Contatti".
 * Unica implementazione per dialog e pagina completa (P-UX-03), riusabile da GI Soggetti.
 */
import React, { useRef } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ITALIAN_PROVINCES } from "@/lib/constants/italianProvinces";
import { VatFromTaxCodeAction } from "./VatFromTaxCodeAction";
import { ComuneAssist } from "./ComuneAssist";

const FieldError = ({ msg, testId }) => (msg ? <div className="text-xs text-suite-error mt-1" data-testid={testId}>{msg}</div> : null);

export function AnagraficaFiscaleSection({ form, setForm, validation, readOnly = false, pecRequired = true, nameLabel = "Ragione sociale", tid = "anagrafica" }) {
  const vatRef = useRef(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const field = (name, extra = {}) => ({
    value: form[name] || "",
    onBlur: () => validation.onBlurField(name),
    "data-testid": `${tid}-${extra.tidName || name}`,
  });
  const err = validation.visible;

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>{nameLabel} *</Label>
          <Input {...field("name")} onChange={(e) => set({ name: e.target.value })} />
        </div>
        <div>
          <Label>Codice fiscale</Label>
          <Input {...field("tax_code", { tidName: "cf" })} onChange={(e) => set({ tax_code: e.target.value.toUpperCase() })} />
          <FieldError msg={err.tax_code} testId={`${tid}-cf-error`} />
        </div>
        <div>
          <Label>Partita IVA</Label>
          <Input ref={vatRef} {...field("vat", { tidName: "piva" })} onChange={(e) => set({ vat: e.target.value.replace(/\D/g, "").slice(0, 11) })} />
          <FieldError msg={err.vat} testId={`${tid}-piva-error`} />
          {!readOnly && <VatFromTaxCodeAction taxCode={form.tax_code} vat={form.vat} vatRef={vatRef} onCopy={(v) => set({ vat: v })} testIdPrefix={`${tid}-piva-from-cf`} />}
        </div>
        <div>
          <Label>PEC (posta certificata){pecRequired ? " *" : ""}</Label>
          <Input type="email" {...field("pec")} onChange={(e) => set({ pec: e.target.value })} placeholder="azienda@pec.it" />
          <FieldError msg={err.pec} testId={`${tid}-pec-error`} />
        </div>
        <div>
          <Label>Iscrizione REA n.</Label>
          <Input {...field("rea_number", { tidName: "rea" })} onChange={(e) => set({ rea_number: e.target.value })} placeholder="RM-000000" />
        </div>
      </div>

      <div className="rounded-lg border border-suite-neutral-200 p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-suite-neutral-800">
          <MapPin size={14} className="text-suite-primary" /> Sede legale
        </div>
        <div className="grid md:grid-cols-6 gap-4">
          <div className="md:col-span-6">
            <Label>Indirizzo (via e civico)</Label>
            <Input {...field("address")} onChange={(e) => set({ address: e.target.value })} placeholder="Via Roma 1" />
          </div>
          <div className="md:col-span-1">
            <Label>CAP</Label>
            <Input {...field("cap")} onChange={(e) => set({ cap: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="00100" maxLength={5} />
            <FieldError msg={err.cap} testId={`${tid}-cap-error`} />
          </div>
          <div className="md:col-span-4">
            <Label>Comune</Label>
            <Input {...field("comune")} onChange={(e) => set({ comune: e.target.value })} placeholder="Roma" />
            {!readOnly && <ComuneAssist comune={form.comune} provincia={form.provincia} cap={form.cap} onApply={set} testIdPrefix={`${tid}-comune-assist`} />}
          </div>
          <div className="md:col-span-1">
            <Label>Provincia</Label>
            <Select value={form.provincia || ""} onValueChange={(v) => { set({ provincia: v }); validation.onBlurField("provincia"); }}>
              <SelectTrigger data-testid={`${tid}-provincia`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {ITALIAN_PROVINCES.map((p) => <SelectItem key={p.code} value={p.code}>{p.code} — {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <FieldError msg={err.provincia} testId={`${tid}-provincia-error`} />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Email</Label>
          <Input type="email" {...field("email")} onChange={(e) => set({ email: e.target.value })} placeholder="info@azienda.it" />
        </div>
        <div>
          <Label>Telefono</Label>
          <Input {...field("phone")} onChange={(e) => set({ phone: e.target.value })} placeholder="+39 06 1234567" />
        </div>
      </div>
    </div>
  );
}

export default AnagraficaFiscaleSection;
